import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import classnames from 'classnames';
import autobind from 'core-decorators/lib/autobind';
import ShareButtons, { SHARE_MODES } from '@wix/wix-vod-shared/src/widget/vod-player/components/share-buttons-new/share-buttons';
import ClosableOverlay from '@wix/wix-vod-shared/src/widget/vod-player/components/closable-overlay/closable-overlay';
import Animate from '@wix/wix-vod-shared/src/widget/ui-components/animate/animate';
import i18n from '@wix/wix-vod-shared/src/common/i18n';
import focus from '@wix/wix-vod-shared/src/widget/utils/accessibility-focus';
import socialNetworks from '@wix/wix-vod-shared/src/widget/constants/social-networks';
import {
  getShareMode,
  isShareOverlayOpen,
  getInstanceInfo,
  getVideoUrl,
  getChannelUrl,
} from 'widget/selectors/share-overlay';
import { getCompId, getCurrentPageId, canShowChannelShare, canShareVideo } from 'shared/selectors/app-settings';
import { isEmbedEnabled } from 'widget/selectors/embed';
import { isPreviewOrEditorMode } from 'widget/components/preview-prompt/preview-prompt';
import { closeShareOverlay } from 'widget/redux/client/actions/share-overlay';
import { notForPreview } from 'widget/utils/not-for-preview';
import WixSDK from 'shared/utils/wix-sdk';
import viewModes from 'shared/constants/view-modes';
import bi from 'shared/bi/bi';
import reportStats from 'widget/redux/server/actions/stats/report';
import STATS_EVENTS from 'shared/constants/stats/events';

import EmbedForm from './embed-form/embed-form';

import translations from 'messages/widget';

// TODO: fix fade out animation
import styles from './styles.styl';

const TRANSLATIONS_MODE_MAP = {
  [SHARE_MODES.VIDEO]: translations.shareThisVideo,
  [SHARE_MODES.CHANNEL]: translations.shareThisChannel,
  CHANNEL_AND_VIDEO: translations.shareChannelOrVideo,
};

const SHARE_EMBED_ENUM = 'embed';

@connect((state, ownProps) => ({
  shareMode: getShareMode(state),
  canShareContent: !isPreviewOrEditorMode(),
  isChannelShareVisible: _.isUndefined(ownProps.canShareChannel) ?
    canShowChannelShare(state) :
    ownProps.canShareChannel,
  isVideoShareVisible: _.isUndefined(ownProps.canShareVideo) ?
    canShareVideo(state) :
    ownProps.canShareVideo,
  instanceInfo: getInstanceInfo(state),
  isShareOverlayOpen: isShareOverlayOpen(state),
  shouldShowEmbedButton: isEmbedEnabled(ownProps),
  compId: getCompId(state),
  sitePageId: getCurrentPageId(state),

  // TODO: get links to media share server
  videoUrl: getVideoUrl(state, ownProps),
  channelUrl: getChannelUrl(state),
  viewMode: WixSDK.Utils.getViewMode(),
}), {
  closeShareOverlay,
  reportStats,
})
class ShareOverlay extends React.Component {

  static propTypes = {
    channelData: PropTypes.object,
    videoItem: PropTypes.object,
    viewMode: PropTypes.string,
    instanceInfo: PropTypes.object,
    canShareContent: PropTypes.bool,
    isShareOverlayOpen: PropTypes.bool,
    shouldShowEmbedButton: PropTypes.bool,
    isChannelShareVisible: PropTypes.bool,
    isVideoShareVisible: PropTypes.bool,
    canShareChannel: PropTypes.bool,
    canShareVideo: PropTypes.bool,
    shareMode: PropTypes.oneOf([SHARE_MODES.CHANNEL, SHARE_MODES.VIDEO]),

    compId: PropTypes.string,
    sitePageId: PropTypes.string,

    videoUrl: PropTypes.string,
    channelUrl: PropTypes.string,

    closeShareOverlay: PropTypes.func,
    onShare: PropTypes.func,
    reportStats: PropTypes.func,
  };

  static defaultProps = {
    onShare: _.noop,
  };

  constructor(props) {
    super(props);

    this.state = {
      isEmbedOpen: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { isShareOverlayOpen, viewMode } = this.props;

    // will be closed
    if (isShareOverlayOpen && !nextProps.isShareOverlayOpen) {
      this.closeEmbedForm();
    }

    // back to editor from preview
    if (viewMode !== viewModes.EDITOR && nextProps.viewMode === viewModes.EDITOR) {
      this.closeShareOverlay();
    }
  }

  componentDidUpdate(prevProps) {
    const { isShareOverlayOpen } = this.props;

    // is opened
    if (!prevProps.isShareOverlayOpen && isShareOverlayOpen) {
      focus(this.containerRef);
    }
  }

  componentWillUnmount() {
    const { isShareOverlayOpen, closeShareOverlay } = this.props;

    if (isShareOverlayOpen) {
      closeShareOverlay();
    }
  }

  getTitleByShareMode(shareMode) {
    const { isChannelShareVisible, isVideoShareVisible } = this.props;
    if (isChannelShareVisible && isVideoShareVisible) {
      return i18n.t(TRANSLATIONS_MODE_MAP.CHANNEL_AND_VIDEO);
    }

    const translation = TRANSLATIONS_MODE_MAP[shareMode];
    return translation ? i18n.t(translation) : '';
  }

  getTranslations(shareMode) {
    return {
      titleText: this.getTitleByShareMode(shareMode),
      shareChannelText: i18n.t(translations.shareWholeChannel),
      shareVideoText: i18n.t(translations.desktopShareVideo),
      twitterDescriptionText: i18n.t(translations.twitterDescription, { title: '{title}', siteUrl: '{siteUrl}' }),
      pinterestDescriptionText: i18n.t(translations.pinterestDescription, {
        title: '{title}',
        description: '{description}',
      }),
      tumblrDescriptionText: i18n.t(translations.tumblrDescription, { title: '{title}', siteUrl: '{siteUrl}' }),
      linkCopiedText: i18n.t(translations.linkCopied),

      // tooltips
      embedTooltipText: i18n.t(translations.embedVideo),
      facebookTooltipText: i18n.t(translations.facebook),
      linkTooltipText: i18n.t(translations.copyLink),
      pinterestTooltipText: i18n.t(translations.pinterest),
      tumblrTooltipText: i18n.t(translations.tumblr),
      twitterTooltipText: i18n.t(translations.twitter),
    };
  }

  getAriaLabels() {
    return {
      facebookAriaLabel: i18n.t(translations.shareOn, { network: 'Facebook' }),
      twitterAriaLabel: i18n.t(translations.shareOn, { network: 'Twitter' }),
      pinterestAriaLabel: i18n.t(translations.shareOn, { network: 'Pinterest' }),
      tumblrAriaLabel: i18n.t(translations.shareOn, { network: 'Tumblr' }),
      linkAriaLabel: i18n.t(translations.getLink),
      embedAriaLabel: i18n.t(translations.getEmbedCode),
      moreAriaLabel: i18n.t(translations.showMore),
    };
  }

  @autobind
  @notForPreview
  openEmbedForm({ shareType }) {
    this.reportStats({
      networkName: SHARE_EMBED_ENUM,
      shareType,
    });

    this.setState({
      isEmbedOpen: true,
    });
  }

  @autobind
  closeEmbedForm() {
    if (!this.state.isEmbedOpen) {
      return;
    }
    this.setState({
      isEmbedOpen: false,
    });
  }

  closeShareOverlay() {
    this.props.closeShareOverlay();
  }

  @autobind
  saveRef(ref) {
    this.containerRef = ref;
  }

  @autobind
  @notForPreview
  handleShare({ networkName, shareType }) {
    const { channelData } = this.props;

    this.reportStats({ networkName, shareType });

    bi.log('widget.vid.share.clicked', {
      button: networkName,
      shareType,
      channelID: channelData.id,
    });
  }

  reportStats({ networkName, shareType }) {
    const { reportStats, videoItem, channelData } = this.props;

    if (shareType === SHARE_MODES.VIDEO) {
      reportStats(STATS_EVENTS.VIDEO_SHARE, {
        channel: channelData,
        videoItem,
        network: {
          name: networkName,
        },
      });
    } else {
      reportStats(STATS_EVENTS.CHANNEL_SHARE, {
        channel: channelData,
        network: {
          name: networkName,
        },
      });
    }
  }

  @autobind
  @notForPreview
  handleLinkCopied({ shareType }) {
    const { channelData } = this.props;

    this.reportStats({
      networkName: socialNetworks.LINK,
      shareType,
    });

    bi.log('widget.vid.share.clicked', {
      button: 'link',
      shareType,
      channelID: channelData.id,
    });
  }

  canShowSelect() {
    const { isChannelShareVisible, isVideoShareVisible, videoItem } = this.props;
    return isChannelShareVisible && isVideoShareVisible && Boolean(videoItem);
  }

  getShareMode() {
    const { videoItem, shareMode } = this.props;
    // it should always be in share channel mode if has no video
    return videoItem ? shareMode : SHARE_MODES.CHANNEL;
  }

  render() {
    const {
      instanceInfo,
      canShareContent,
      channelData,
      videoItem,
      isShareOverlayOpen,
      closeShareOverlay,
      shouldShowEmbedButton,
      videoUrl,
      channelUrl,
      compId,
      sitePageId,
      shareMode,
    } = this.props;

    const classNames = classnames('share-overlay-container', styles.wrapper, {
      [styles.active]: isShareOverlayOpen,
    });

    const { isEmbedOpen } = this.state;

    return (
      <div className={classNames}
           data-hook="share-overlay"
           aria-hidden={!isShareOverlayOpen}
           ref={this.saveRef}>
        <Animate type="fade-in-out"
                 className={`share-overlay-animate ${styles.content}`}
                 when={isShareOverlayOpen}>
          <ClosableOverlay onClose={closeShareOverlay}
                           closeOnBodyClick
                           isFullWidth
                           closeButtonAriaLabel={i18n.t(translations.closeShare)}>
            {
              <ShareButtons channelData={channelData}
                            videoItem={videoItem}
                            shareMode={this.getShareMode()}
                            instanceInfo={instanceInfo}
                            canShareContent={canShareContent}
                            onEmbedClicked={this.openEmbedForm}
                            canShowSelect={this.canShowSelect()}
                            onShare={this.handleShare}
                            onLinkCopied={this.handleLinkCopied}
                            shouldShowEmbedButton={shouldShowEmbedButton}
                            videoUrl={videoUrl}
                            channelUrl={channelUrl}
                            {...this.getTranslations(shareMode)}
                            {...this.getAriaLabels()}>
                {
                  isEmbedOpen && (
                    <EmbedForm dataHook="embed-form"
                               onBack={this.closeEmbedForm}
                               channelId={channelData.id}
                               videoId={videoItem.id}
                               compId={compId}
                               sitePageId={sitePageId} />
                  )
                }
              </ShareButtons>
            }
          </ClosableOverlay>
        </Animate>
      </div>
    );
  }
}

export default ShareOverlay;



// WEBPACK FOOTER //
// ./src/widget/containers/share-overlay/share-overlay.js